import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const HELP_TEXTS: Record<string, { title: string; text: string }> = {
  'admin-company': {
    title: 'Firmendaten',
    text: 'Pflegen Sie hier Ihre Unternehmensangaben. Diese erscheinen als Fußzeile in jedem PDF-Export (Expertenprofil). Rechtliche Angaben wie Amtsgericht und Handelsregisternummer sind für das Impressum relevant.',
  },
  'admin-customize': {
    title: 'Anpassungen',
    text: 'Passen Sie das Erscheinungsbild von INSIGHT an: Logo, Farben, Sidebar-Breite und Login-Hintergrund. Änderungen werden sofort für alle Benutzer sichtbar.',
  },
  'admin-users': {
    title: 'Benutzerverwaltung',
    text: 'Verwalten Sie alle Benutzer der Plattform. Sie können neue Benutzer anlegen, Rollen zuweisen und Konten aktivieren oder deaktivieren.',
  },
  'admin-master-data': {
    title: 'Stammdaten',
    text: 'Verwalten Sie Referenzlisten wie Abteilungen, Standorte, Kostenstellen, Stellenbezeichnungen und Skill-Kategorien. Diese Listen werden in anderen Bereichen als Auswahloptionen verwendet.',
  },
  'admin-ai-settings': {
    title: 'KI-Einstellungen',
    text: 'Hinterlegen Sie hier Ihren Anthropic API-Key, um den KI-Assistenten zu aktivieren. Der Key wird sicher gespeichert und niemals zurückgegeben. Ohne Key stehen nur die statischen Hilfetexte zur Verfügung.',
  },
  'crm-companies': {
    title: 'Unternehmen',
    text: 'Verwalten Sie Ihre Geschäftskontakte und Unternehmen. Filtern Sie nach Gesellschaften, Branchen oder nutzen Sie die A-Z Suche. Klicken Sie auf ein Unternehmen für Details, Kontakthistorie, Projektanfragen und externe Verbindungen (Lexware, Clockodo).',
  },
  'crm-contacts': {
    title: 'Kontakte',
    text: 'Ihre Ansprechpartner und Kontaktpersonen. Kontakte können mit Unternehmen verknüpft und über Microsoft 365 synchronisiert werden. Unter "Outlook Daten" sehen Sie E-Mails und Kalendereinträge zum Kontakt.',
  },
  'crm-deals': {
    title: 'Deals / Vorgänge',
    text: 'Verwalten Sie Ihre Verkaufschancen. Jeder Deal hat einen Wert, eine Stage in Ihrer Pipeline und einen Status (Offen, Gewonnen, Verloren). Nutzen Sie das Kanban-Board für eine visuelle Übersicht. Deals können mit Kontakten, Unternehmen und Projektanfragen verknüpft werden.',
  },
  'crm-reports': {
    title: 'CRM Reports',
    text: 'Analysieren Sie Ihre Vertriebsleistung: Win/Loss-Rate, Umsatz-Trend, Verlustgründe und Aktivitätsstatistiken. Wählen Sie den Zeitraum oben aus.',
  },
  'crm-company-detail': {
    title: 'Unternehmensdetail',
    text: 'Die Detailseite zeigt alle Informationen zum Unternehmen: Stammdaten, Kontakte, Kontakthistorie, Projektanfragen, ERP-Belege, Verträge und externe Verbindungen. Über die Kontakthistorie können Sie Aktivitäten dokumentieren und Projektanfragen zuordnen.',
  },
  'crm-contact-detail': {
    title: 'Kontaktdetail',
    text: 'Details zum Kontakt mit Kommunikationsdaten, Kontakthistorie und Outlook-Integration. Im Tab "Outlook Daten" sehen Sie E-Mails und Kalendereinträge die mit der E-Mail-Adresse des Kontakts verknüpft sind. Diese können Sie in die Kontakthistorie übernehmen.',
  },
  'crm-kanban': {
    title: 'Kanban-Board',
    text: 'Visuelles Pipeline-Management per Drag & Drop. Verschieben Sie Deals zwischen Stages, sehen Sie den Gesamtwert pro Stage und filtern Sie nach Pipeline oder Zeitraum.',
  },
  'crm-admin': {
    title: 'CRM Administration',
    text: 'Konfigurieren Sie Gesellschaften (Marken/Unternehmen mit Farben), Branchen, Kontotypen, Pipelines und Integrationen. Unter Berechtigungen steuern Sie den Zugriff auf CRM-Funktionen pro Benutzer.',
  },
  'crm-project-requests': {
    title: 'Projektanfragen',
    text: 'Verwalten Sie projektbezogene Anfragen mit Details wie Einsatzort, Laufzeit, Auslastung und Stundensätze. Projektanfragen können mit Clockodo-Projekten und Aktivitäten aus der Kontakthistorie verknüpft werden.',
  },
  'expert-profile': {
    title: 'Expertenprofil',
    text: 'Ihr persönliches Kompetenzprofil. Fügen Sie Skills, Projekterfahrungen, Zertifizierungen und Sprachen hinzu. Das Profil kann als PDF oder Word-Dokument exportiert werden.',
  },
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pageKey: string;
}

export function HelpPanel({ isOpen, onClose, pageKey }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: aiConfig } = useQuery({
    queryKey: ['settings', 'ai-config'],
    queryFn: () => api.get<{ configured: boolean }>('/settings/ai-config').then(r => r.data),
    staleTime: 60_000,
    enabled: isOpen,
  });

  const aiConfigured = aiConfig?.configured ?? true; // optimistic default until loaded

  const helpContent = HELP_TEXTS[pageKey] ?? {
    title: 'Hilfe',
    text: 'Wie kann ich Ihnen helfen? Stellen Sie eine Frage im Chat unten.',
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isOpen) return null;

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const res = await api.post<{ reply: string }>('/help/chat', {
        messages: newMessages,
        context: helpContent.title,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch {
      setError('KI-Assistent nicht verfügbar. Bitte prüfen Sie die Konfiguration.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1040,
          background: 'rgba(0,0,0,0.2)',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 340, zIndex: 1050,
        background: 'var(--color-bg-card)',
        borderLeft: '1px solid var(--color-border)',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'inherit',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 700, fontSize: '1rem' }}>❓ Hilfe</span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.25rem', color: 'var(--color-text-secondary)', padding: '0.25rem',
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
          <p style={{ margin: '0 0 0.375rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            📖 {helpContent.title}
          </p>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--color-text)', lineHeight: 1.5 }}>
            {helpContent.text}
          </p>
          {pageKey.startsWith('crm') && (
            <button
              onClick={() => {
                localStorage.removeItem('crm_tour_completed');
                onClose();
                navigate('/crm');
              }}
              style={{
                marginTop: '0.75rem', padding: '0.375rem 0.75rem',
                fontSize: '0.75rem', fontWeight: 600,
                background: 'transparent', color: 'var(--color-primary)',
                border: '1px solid var(--color-primary)', borderRadius: '6px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 4v6h6" />
                <path d="M3.5 10A7 7 0 1 1 1 7" />
              </svg>
              CRM Tour neu starten
            </button>
          )}
        </div>

        {/* Chat */}
        <div style={{ padding: '1rem 1.25rem 0.5rem', flexShrink: 0 }}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            🤖 KI-Assistent
          </p>
        </div>

        {!aiConfigured ? (
          /* AI not configured — show hint */
          <div style={{
            flex: 1,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem 1.25rem', gap: '0.75rem', textAlign: 'center',
          }}>
            <span style={{ fontSize: '2rem' }}>🔑</span>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              Der KI-Assistent ist noch nicht eingerichtet.
            </p>
            <button
              onClick={() => { onClose(); navigate('/admin/ai-settings'); }}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--color-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.8125rem',
              }}
            >
              → Admin: KI-Einstellungen
            </button>
          </div>
        ) : (
          <>
            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {messages.length === 0 && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                  Stellen Sie eine Frage zur Bedienung von INSIGHT…
                </p>
              )}
              {messages.map((msg, i) => (
                <div key={i} style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: msg.role === 'user' ? 'var(--color-primary)' : 'var(--color-bg)',
                  color: msg.role === 'user' ? '#fff' : 'var(--color-text)',
                  border: msg.role === 'assistant' ? '1px solid var(--color-border)' : 'none',
                  borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.8125rem',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.content}
                </div>
              ))}
              {loading && (
                <div style={{ alignSelf: 'flex-start', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                  Denkt nach…
                </div>
              )}
              {error && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-danger, #dc2626)' }}>{error}</div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{
              padding: '0.75rem 1.25rem 1rem',
              borderTop: '1px solid var(--color-border)',
              display: 'flex', gap: '0.5rem', flexShrink: 0,
            }}>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendMessage(); } }}
                placeholder="Frage eingeben… (Enter zum Senden)"
                rows={2}
                style={{
                  flex: 1,
                  resize: 'none',
                  padding: '0.5rem 0.6rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text)',
                  fontFamily: 'inherit',
                }}
              />
              <button
                onClick={() => void sendMessage()}
                disabled={!input.trim() || loading}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: 'var(--color-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  alignSelf: 'flex-end',
                  opacity: (!input.trim() || loading) ? 0.5 : 1,
                }}
              >
                ➤
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
